import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AxiosResponse } from 'axios'
import { chunk, omit, orderBy } from 'lodash'

import { DuckDBSchemaType, Tag } from '@types'
import { ApiListResponse } from '../utils/types'

import { TAG_FIELD } from '@/const'

import {
  addStoreListItem,
  prepareResponseError,
  removeStoreListItem,
  trimObjectValues,
  updateStoreListItem,
} from '../utils/helpers'

import { DuckDBService } from '@/services/duckdb'

import api from '@/store/api'
import { useRepositoriesStore } from '@/store/repositories/index'

import { TABLE_NAME } from './utils/const'

export { TABLE_NAME }

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [TAG_FIELD.ID]: 'VARCHAR',
  [TAG_FIELD.TAG_NAME]: 'VARCHAR',
  [TAG_FIELD.TAG_VALUE]: 'VARCHAR',
}

// Max chunk size for bulk requests
const BULK_CHUNK_SIZE = 500

export const useTagsStore = defineStore('tags', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const repositoriesStore = useRepositoriesStore()

  const list = ref<Tag[]>([])

  const initFlag = ref(false)
  const trigger = ref(0)

  const loading = ref(true)
  const loadingAction = ref(false)

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS

  const getList = computed<Tag[]>(() =>
    orderBy(
      list.value,
      ['tag_name', 'tag_value'].map(
        field => (item: Tag) =>
          item[field as keyof Tag]?.toString().toLowerCase(),
      ),
      ['asc'],
    ),
  )

  // SETTERS

  const setList = async (data: Tag[]) => {
    list.value = data
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await duckdbInstance.pushData(TABLE_NAME, data, TABLE_SCHEMA)
    trigger.value++
  }

  const fetch = async (): Promise<Tag[]> => {
    loading.value = true
    try {
      const result = await api.get<ApiListResponse<Tag[]>>('asset_tags', {
        params: {
          repository_id: repositoryId.value,
        },
        signal: abortController.signal,
      })
      const data = result.data.data
      await setList(data)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      initFlag.value = true
      loading.value = false
    }
  }

  const store = async (tag: Tag): Promise<Tag> => {
    loadingAction.value = true
    try {
      const trimmedTag = trimObjectValues(tag, ['tag_name', 'tag_value'])
      const result = await api.post<Tag, AxiosResponse<Tag>, Partial<Tag>>(
        'asset_tags',
        { ...omit(trimmedTag, 'id'), repository_id: repositoryId.value },
      )
      const data = result.data
      addStoreListItem<Tag>(list.value, data)
      await duckdbInstance.pushData(TABLE_NAME, [data], TABLE_SCHEMA)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const bulkStore = async (
    tags: Partial<Tag>[],
    onChunk?: (chunkTags: Partial<Tag>[]) => void,
  ): Promise<Tag[]> => {
    const repoId = repositoryId.value
    // fail before the first request: the backend scopes the whole batch to this id
    if (!repoId) throw Error('No repository selected')
    // an import is many sequential requests and can outlive the active repository; hold the
    // signal this loop started with, because cancel() swaps in a fresh controller
    const { signal } = abortController
    loadingAction.value = true
    const created: Tag[] = []
    try {
      // sequential: parallel chunks would recreate the burst
      for (const chunkTags of chunk(tags, BULK_CHUNK_SIZE)) {
        if (signal.aborted) break
        const result = await api.post<
          Tag[],
          AxiosResponse<ApiListResponse<Tag[]>>,
          Partial<Tag>[]
        >(
          'asset_tags',
          chunkTags.map(tag => ({
            ...omit(trimObjectValues(tag as Tag, ['tag_name', 'tag_value']), 'id'),
            repository_id: repoId,
          })),
          { params: { repository_id: repoId }, signal },
        )
        const data = result.data.data
        // switching repository calls clear() on this store, so stop before writing one
        // repository's tags into another's list and duckdb table
        if (signal.aborted) break
        // the server has committed this chunk: record it and unstage the rows before the
        // local cache update, so a duckdb failure cannot under-report what actually landed
        created.push(...data)
        onChunk?.(chunkTags)
        data.forEach(tag => addStoreListItem<Tag>(list.value, tag))
        await duckdbInstance.pushData(TABLE_NAME, data, TABLE_SCHEMA)
      }
      return created
    } catch (e) {
      const message = prepareResponseError(e)
      // every chunk is its own request, so the earlier ones stay committed; say so, because
      // handleGridChanges sees one rejected promise here and never fires partialSuccess
      throw Error(
        created.length
          ? `${created.length} of ${tags.length} tags were imported, then: ${message}`
          : message,
      )
    } finally {
      loadingAction.value = false
    }
  }

  const update = async (tag: Tag): Promise<Tag | undefined> => {
    loadingAction.value = true
    try {
      const { id } = tag
      const result = await api.put<Tag, AxiosResponse<Tag>, Tag>(
        `asset_tags/${id}`,
        tag,
      )
      const data = result.data
      updateStoreListItem<Tag>(list.value, data)
      await duckdbInstance.updateData(TABLE_NAME, id, data, TABLE_SCHEMA)
      return data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const destroy = async (id: string): Promise<void> => {
    loadingAction.value = true
    try {
      await api.delete(`asset_tags/${id}`)
      removeStoreListItem<Tag>(list.value, id)
      await duckdbInstance.deleteData(TABLE_NAME, id, TABLE_SCHEMA)
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    setList([])
    initFlag.value = false
    trigger.value = 0
  }

  return {
    initFlag,
    trigger,

    loading,
    loadingAction,

    getList,

    fetch,

    store,
    update,
    destroy,

    bulkStore,

    cancel,
    clear,
  }
})
